import React, { useCallback, useMemo, useState } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { FormGroup } from '../../../../../../components/form/FormGroup';
import InputLabel from '../../../../../../components/form/InputLabel';
import Button from '../../../../../../components/button';
import { useTranslation } from 'react-i18next';
import { useToasts } from '../../../../../../context/Toast';
import { useMutationErrorHandler } from '../../../../../../hooks/useMutationErrorHandler';
import { SelectMultipleEmployees } from '../../../../components/Selects/SelectMultipleEmployees';
import { useGoalsEmployeesQuery } from '../../../../__generated__/graphql';
import ServerError from '../../../../../../components/ServerError';
import { mapEmployeeOption, toObjectiveIdInput } from '../mappings';
import { usePushObjectives } from '../GoalSidebar/useObjectiveMutations';
import { GoalId } from '../types';
import '../GoalsModal.css';

type PushGoalModalProps = {
  visible: boolean;
  onClose: () => void;
  goalId: GoalId;
};

const i18prefix = 'performanceReview.goals2.pushGoal';

export const PushGoalModal: React.FC<PushGoalModalProps> = ({
  visible,
  onClose,
  goalId,
}) => {
  const { t } = useTranslation();
  const { addSuccess } = useToasts();
  const errorHandler = useMutationErrorHandler();
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  const [setAsParent, setSetAsParent] = useState<boolean>(true);
  const [doNotCreateDuplicates, setDoNotCreateDuplicates] =
    useState<boolean>(true);
  const [pushObjectives, { loading: isPushing }] = usePushObjectives();
  const { data, error, loading } = useGoalsEmployeesQuery({
    skip: !visible,
  });

  const employeeOptions = useMemo(() => {
    const employees = data?.manager?.employees;
    if (!employees) {
      return [];
    }
    return employees.map(mapEmployeeOption);
  }, [data?.manager?.employees]);

  const handleClose = useCallback(() => {
    setSelectedEmployeeIds([]);
    setSetAsParent(true);
    setDoNotCreateDuplicates(true);
    onClose();
  }, [onClose]);

  const renderGoalsModalError = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title>{t(`${i18prefix}.title`)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ServerError />
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="outline-secondary"
          label={t(`${i18prefix}.cancel`)}
          onClick={handleClose}
        />
      </Modal.Footer>
    </>
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (selectedEmployeeIds.length === 0) {
        return;
      }
      pushObjectives({
        variables: {
          input: {
            objectiveIds: [toObjectiveIdInput(goalId)],
            targetEmployeeIds: selectedEmployeeIds,
            setAsParent,
            doNotCreateDuplicates,
          },
        },
      })
        .then((result) => {
          const createdRecordIds =
            result.data?.objectives?.pushObjectives?.createdRecordIds;
          if (!createdRecordIds) {
            return;
          }
          addSuccess(
            t('performanceReview.goals2.toastPushSuccess', {
              count: createdRecordIds.length,
            }),
          );
          handleClose();
        })
        .catch(errorHandler);
    },
    [
      selectedEmployeeIds,
      setAsParent,
      doNotCreateDuplicates,
      pushObjectives,
      goalId,
      handleClose,
      addSuccess,
      t,
      errorHandler,
    ],
  );

  if (!visible) {
    return null;
  }

  return (
    <Modal
      show={visible}
      onHide={handleClose}
      size="lg"
      centered
      backdropClassName="goals-modal-backdrop"
      contentClassName="goals-modal-content border">
      {error ? (
        renderGoalsModalError()
      ) : (
        <form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <div>
              <Modal.Title>{t(`${i18prefix}.title`)}</Modal.Title>
              <div>{t(`${i18prefix}.subtitle`)}</div>
            </div>
          </Modal.Header>

          <Modal.Body className="p-4">
            <FormGroup>
              <InputLabel label={t(`${i18prefix}.selectEmployeesLabel`)} />
              <SelectMultipleEmployees
                values={selectedEmployeeIds}
                options={employeeOptions}
                onChange={setSelectedEmployeeIds}
                placeholder={t(`${i18prefix}.selectEmployeesPlaceholder`)}
                renderMode="block"
                isNameVisible={true}
                isDisabled={loading}
              />
            </FormGroup>
            <FormGroup>
              <Form.Check
                type="checkbox"
                id="pushGoal-setAsParent"
                label={t(`${i18prefix}.setAsParent`)}
                checked={setAsParent}
                onChange={(e) => setSetAsParent(e.target.checked)}
              />
              <Form.Text className="text-muted">
                {setAsParent
                  ? t(`${i18prefix}.copyDescriptionWithParent`)
                  : t(`${i18prefix}.copyDescriptionWithoutParent`)}
              </Form.Text>
            </FormGroup>
            <FormGroup>
              <Form.Check
                type="checkbox"
                id="pushGoal-doNotCreateDuplicates"
                label={t(`${i18prefix}.doNotCreateDuplicates`)}
                checked={doNotCreateDuplicates}
                onChange={(e) => setDoNotCreateDuplicates(e.target.checked)}
              />
              <Form.Text className="text-muted">
                {doNotCreateDuplicates
                  ? t(`${i18prefix}.doNotCreateDuplicatesWith`)
                  : t(`${i18prefix}.doNotCreateDuplicatesWithout`)}
              </Form.Text>
            </FormGroup>
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="outline-secondary"
              label={t(`${i18prefix}.cancel`)}
              onClick={handleClose}
              disabled={isPushing}
            />
            <Button
              variant="primary"
              label={t(`${i18prefix}.pushGoal`)}
              type="submit"
              isProcessing={isPushing}
              disabled={
                isPushing || loading || selectedEmployeeIds.length === 0
              }
            />
          </Modal.Footer>
        </form>
      )}
    </Modal>
  );
};
